import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  appShellBodyRecipe,
  appShellColumnRecipe,
  appShellHeaderRecipe,
  appShellMainRecipe,
  appShellPageRecipe,
  appShellRecipe,
  type AppShellPageRecipeProps,
} from '../recipes/app-shell.recipe';

export interface AppShellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Down the left, outside the scrolling column: the nav, and any rail beside it. */
  sidebar?: React.ReactNode;
  /** The top bar's contents — search, the bell, the account menu. */
  header?: React.ReactNode;
  /**
   * Rendered in the column but outside the header-and-main flow: the toast
   * stack, the phone-width nav drawer. Positioned by their own rules, so
   * where they sit in the tree does not move the page.
   */
  floating?: React.ReactNode;
  /** `main`'s inset. */
  padding?: AppShellPageRecipeProps['padding'];
  /** The page. */
  children?: React.ReactNode;
}

/**
 * The signed-in app's frame: sidebar, top bar, page.
 *
 *   <AppShell
 *     sidebar={<Sidebar />}
 *     header={<><Breadcrumbs items={crumbs} /><SearchBar /></>}
 *     floating={<Toaster />}
 *   >
 *     {children}
 *   </AppShell>
 *
 * The `<header>` and `<main>` landmarks live here, once, so every screen
 * gets the same two and a screen reader can jump between them. What decides
 * which sidebar shows, or how wide, is the layout's business and stays in
 * the app; this only puts what it is handed where it goes.
 */
export const AppShell = React.forwardRef<HTMLDivElement, AppShellProps>(
  function AppShell(
    { sidebar, header, floating, padding, className, children, ...props },
    ref
  ) {
    return (
      <div ref={ref} className={cn(appShellRecipe(), className)} {...props}>
        {sidebar}
        <div className={appShellColumnRecipe()}>
          <div className={appShellBodyRecipe()}>
            {header !== undefined ? (
              // design-tokens-ignore: owned by AppShell
              <header className={appShellHeaderRecipe()}>{header}</header>
            ) : null}
            {/* design-tokens-ignore: owned by AppShell */}
            <main className={appShellMainRecipe()}>
              <div className={appShellPageRecipe({ padding })}>{children}</div>
            </main>
          </div>
          {floating}
        </div>
      </div>
    );
  }
);
